import { useEffect, useState, useRef } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Egg, Upload, Loader2, ImageIcon, X } from "lucide-react";

const empty = { name: "", description: "", category_id: "", price: "", discount_price: "", stock: "", unit: "tray", images: [], is_active: true, is_bestseller: false, is_promoted: false };

export default function AdminProducts() {
  usePageMeta("Manage Products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/products"), api.get("/admin/categories")])
      .then(([p, c]) => {
        setProducts(p.data);
        setCategories(c.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, category_id: categories[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", category_id: p.category_id,
      price: p.price, discount_price: p.discount_price || "", stock: p.stock,
      unit: p.unit, images: (p.images && p.images.length) ? p.images : (p.image_id ? [p.image_id] : []), is_active: p.is_active,
      is_bestseller: !!p.is_bestseller, is_promoted: !!p.is_promoted,
    });
    setOpen(true);
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.image_id] }));
      }
      toast.success(files.length > 1 ? `${files.length} images uploaded` : "Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (id) => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== id) }));

  const save = async () => {
    if (!form.name || !form.category_id || !form.price) {
      toast.error("Name, category and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      category_id: form.category_id,
      price: parseFloat(form.price),
      discount_price: form.discount_price ? parseFloat(form.discount_price) : null,
      stock: parseInt(form.stock || 0, 10),
      unit: form.unit || "tray",
      images: form.images,
      is_active: form.is_active,
      is_bestseller: form.is_bestseller,
      is_promoted: form.is_promoted,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const toggleFlag = async (p, field) => {
    const next = !p[field];
    setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, [field]: next } : x)));
    try {
      await api.patch(`/admin/products/${p.id}/flags`, { [field]: next });
      const label = field === "is_bestseller"
        ? (next ? "Added to Bestsellers" : "Removed from Bestsellers")
        : (next ? "Promoted to top" : "Promotion removed");
      toast.success(label);
    } catch (err) {
      setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, [field]: !next } : x)));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Products</h1>
          <p className="mt-1 text-ink-soft">Add and manage your egg listings.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="add-product-btn" disabled={categories.length === 0}>
          <Plus className="mr-1 h-4 w-4" /> Add Product
        </Button>
      </div>

      {categories.length === 0 && (
        <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-700">Create a category first before adding products.</p>
      )}

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : products.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Egg className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-4 font-heading text-lg text-ink">No products yet</p>
          <p className="text-sm text-ink-muted">Add your first product to make it visible on the store.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-border/60 bg-white shadow-ambient">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-cream-alt/60 text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Bestseller</th>
                  <th className="px-4 py-3">Promote</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-cream-alt">
                          {p.image_id ? <img src={fileUrl(p.image_id)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-ink-muted"><ImageIcon className="h-4 w-4" /></div>}
                        </div>
                        <span className="font-medium text-ink">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{catName(p.category_id)}</td>
                    <td className="px-4 py-3 text-ink">
                      {p.discount_price ? <><span className="font-semibold">{rupee(p.discount_price)}</span> <span className="text-xs text-ink-muted line-through">{rupee(p.price)}</span></> : rupee(p.price)}
                    </td>
                    <td className="px-4 py-3"><span className={p.stock <= 5 ? "font-semibold text-destructive" : "text-ink-soft"}>{p.stock}</span></td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.is_active ? "bg-sage/15 text-sage" : "bg-ink/10 text-ink-muted"}`}>
                        {p.is_active ? "Visible" : "Hidden"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Switch checked={!!p.is_bestseller} onCheckedChange={() => toggleFlag(p, "is_bestseller")} data-testid={`toggle-bestseller-${p.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <Switch checked={!!p.is_promoted} onCheckedChange={() => toggleFlag(p, "is_promoted")} data-testid={`toggle-promoted-${p.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(p)} className="text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product images</Label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {form.images.map((id, idx) => (
                  <div key={id} className="relative h-20 w-20 overflow-hidden rounded-xl border border-border bg-cream-alt">
                    <img src={fileUrl(id)} alt="" className="h-full w-full object-cover" />
                    {idx === 0 && <span className="absolute left-1 top-1 rounded bg-brand px-1 text-[9px] font-bold text-ink">Main</span>}
                    <button type="button" onClick={() => removeImage(id)} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-destructive shadow" data-testid={`remove-image-${idx}`} aria-label="Remove image">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-ink-muted transition-colors hover:border-brand hover:text-brand" data-testid="add-product-image">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                  <span className="text-[10px]">Add</span>
                </button>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid="product-image-input" />
              </div>
              <p className="mt-1 text-xs text-ink-muted">Add multiple photos · first image is the main thumbnail · JPG, PNG, WEBP · max 5MB each</p>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="product-name-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹)</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 rounded-xl" data-testid="product-price-input" />
              </div>
              <div>
                <Label>Discount price (₹)</Label>
                <Input type="number" value={form.discount_price} onChange={(e) => setForm({ ...form, discount_price: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Optional" />
              </div>
              <div>
                <Label>Stock</Label>
                <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1.5 rounded-xl" data-testid="product-stock-input" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1.5 rounded-xl" placeholder="tray / dozen / piece" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
              <div>
                <Label>Visible on store</Label>
                <p className="text-xs text-ink-muted">Only visible products appear to customers</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="product-active-switch" />
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
              <div>
                <Label>Show in Bestsellers</Label>
                <p className="text-xs text-ink-muted">You choose which products appear in the Bestsellers row on the home page</p>
              </div>
              <Switch checked={form.is_bestseller} onCheckedChange={(v) => setForm({ ...form, is_bestseller: v })} data-testid="product-bestseller-switch" />
            </div>
            <div className="flex items-center justify-between rounded-xl bg-cream-alt/50 px-4 py-3">
              <div>
                <Label>Promote to top</Label>
                <p className="text-xs text-ink-muted">Promoted products appear first in listings and Bestsellers</p>
              </div>
              <Switch checked={form.is_promoted} onCheckedChange={(v) => setForm({ ...form, is_promoted: v })} data-testid="product-promoted-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand text-ink" data-testid="save-product-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
